import React, { useEffect, useMemo } from 'react';

// Style
import './ChemicalProbes.scss';

// Components
import List from './components/List';
import Search from './components/Search';
import Header from './components/Header';
import HeaderList from './components/HeaderList';
import Breadcrumbs from 'components/Breadcrumbs';
import ScrollButton from 'components/ScrollButton';
import ErrorBoundary from 'components/ErrorBoundary';
import PricesOnAsking from 'components/PricesOnAsking';
import PaginationView from 'components/PaginationView';
import CustomMessage from 'components/Messages/CustomMessage';

// Lib
import { shallowEqual } from 'react-redux';
import { getValueSize } from 'lib/helpers';
import { useRegisterTutorialKey } from 'lib/hooks/useTutorial';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Actions
import * as actionsCreator from './reducer';
import { showMessage } from 'reducers/messages';

// Constants
import { optionsSize } from 'pages/LibrariesCompounds/LibrariesCompounds';

type TFuncArgs = {
	[index: string]: string | number
};

export const ChemicalProbes: React.FC = () => {
	const dispatch = useAppDispatch();
	const registerTutorialKey = useRegisterTutorialKey();

	const {
		data,
		goTo,
		paged,
		dataOld,
		pageSize,
		currPage,
		loading,

		toggleFilter
	} = useAppSelector(
		(store) => {
			return {
				data: store.chemicalProbes.data,
				goTo: store.chemicalProbes.goTo,
				paged: store.chemicalProbes.paged,
				dataOld: store.chemicalProbes.dataOld,
				pageSize: store.chemicalProbes.pageSize,
				currPage: store.chemicalProbes.currPage,
				loading: store.chemicalProbes.loading,

				toggleFilter: store.chemicalProbes.toggleFilter
			};
		},
		shallowEqual
	);

	const total = Math.ceil(data?.length / (pageSize || 20));

	useEffect(() => {
		registerTutorialKey('chemical-probes', () => !!data?.length, "normal", "general");
	}, [data]);

	useEffect(() => {
		dispatch(actionsCreator.getChemicalProbes());
	}, []);

	const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e?.key === 'Enter') {
			e.preventDefault();
			dispatch(actionsCreator.handlerChangePage((goTo || 1) - 1));
		}
	};

	const submitGoTo = () => {
		dispatch(actionsCreator.handlerChangePage((goTo || 1) - 1));
	};

	const onPagePress = (opt: number) => {
		dispatch(actionsCreator.handlerChangePage(opt - 1));
	};

	const onNextPressLast = () => {
		dispatch(actionsCreator.handlerChangePage(total - 1));
	};

	const onPrevPressLast = () => {
		dispatch(actionsCreator.handlerChangePage(0));
	};

	const handlerChangeGoTo = (event: React.ChangeEvent<HTMLInputElement>) => {
		const number = +event?.target?.value > 0 ? +event?.target?.value : 1;

		if (number <= data.length) {
			dispatch(actionsCreator.handlerChangeGoTo(number));
		} else {
			dispatch(
				showMessage({
					type: 'info',
					text: 'This is the last record'
				})
			);
		}
	};

	const handlerChangeSize = (data: TFuncArgs) => {
		dispatch(actionsCreator.handlerChangeSize(data?.value));
	};

	const valueSize = getValueSize({
		opt: pageSize || 20,
		options: optionsSize
	});

	const newData = useMemo(() => {
		if (paged && data?.length) {
			const start = (pageSize || 20) * currPage;
			const end = start + (pageSize || 20);
			return data.slice(start, end);
		}
		return data;
	}, [data, paged, currPage, pageSize]);

	return (
		<ErrorBoundary>
			<ScrollButton />
			<div className="chemical-probes relative">

			{toggleFilter && <div className="chemical-probes__filter-mobile">
					<HeaderList
						isModal
						className="chemical-probes__filter-mobile-header"
					/>
				</div>}
				<div className="wrapper">
					<Breadcrumbs
						isLast
						className="mb10"
					/>

					<Header
						className="mb30"
						count={dataOld?.length}
					/>

					<Search className="mb30" />

					{((total || 0) > 1) && <PaginationView
						className="mb30"

						isWhite
						isShowSelect

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={submitGoTo}
						handlerChangeGoTo={handlerChangeGoTo}

						number={currPage}
						totalPages={total}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={optionsSize}
						handlerChangeSize={handlerChangeSize}
					/>}

					<div className="chemical-probes__content">
						<HeaderList />

						<List
							page={currPage}
							size={pageSize}
							loading={loading}
							data={newData || []}
						/>
					</div>

					{!!newData?.length && ((total || 0) > 1) && <PaginationView
						isWhite

						children={<PricesOnAsking />}

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={submitGoTo}
						handlerChangeGoTo={handlerChangeGoTo}

						number={currPage}
						totalPages={total}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={optionsSize}
						handlerChangeSize={handlerChangeSize}
					/>}

					{newData?.length
						? <CustomMessage
							title="Notice"
							className="mt30"
							text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
						/>
						: null}

				</div>
			</div>
		</ErrorBoundary>
	);
};

export default ChemicalProbes;
